import React from 'react';

interface TabOption {
  value: string;
  label: string;
}

interface TabButtonGroupProps {
  options: TabOption[] | string[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  label?: string;
  multiple?: boolean;
  isRequired?: boolean;
}

const TabButtonGroup: React.FC<TabButtonGroupProps> = ({
  options,
  value,
  onChange,
  label,
  multiple = false,
  isRequired = false,
}) => {
  const handleClick = (optionValue: string) => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      const newValues = values.includes(optionValue)
        ? values.filter((v) => v !== optionValue)
        : [...values, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
    }
  };

  const isSelected = (optionValue: string) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  const getDisplayLabel = (option: TabOption | string) =>
    typeof option === 'string' ? option : option.label;

  const getOptionValue = (option: TabOption | string) =>
    typeof option === 'string' ? option : option.value;

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {label}
          {isRequired && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const optionValue = getOptionValue(option);
          const optionLabel = getDisplayLabel(option);

          return (
            <button
              key={optionValue}
              type="button"
              onClick={() => handleClick(optionValue)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isSelected(optionValue)
                  ? 'bg-accent text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {optionLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TabButtonGroup;
